import { Link } from "react-router-dom";
import { LegalLayout, Section, List } from "./LegalLayout";
import { COMPANY, displayLegalName, formatAddress } from "@/config/company";

/**
 * Termini e condizioni del servizio.
 *
 * ⚠️ BOZZA DA FAR VALIDARE. Il testo è impostato correttamente ma alcune scelte
 * (foro, limitazione di responsabilità, SLA, durata) sono decisioni commerciali:
 * vanno riviste da un legale prima di considerarle vincolanti.
 */
export default function TermsPage() {
  return (
    <LegalLayout
      title="Termini di servizio"
      subtitle="Le condizioni che regolano l'uso della piattaforma AURA da parte dei centri estetici e dei loro collaboratori."
    >
      <Section n={1} title="Chi siamo e cosa regolano questi termini">
        <p>
          Il servizio {COMPANY.brand} è di proprietà di {COMPANY.owner} ed è fornito da{" "}
          <strong>{displayLegalName()}</strong>
          {formatAddress() ? `, con sede in ${formatAddress()}` : ""}
          {COMPANY.vat ? `, P.IVA ${COMPANY.vat}` : ""} (di seguito “il Fornitore”).
        </p>
        <p>
          Questi termini regolano l'accesso e l'uso della piattaforma da parte di imprese e
          professionisti del settore benessere (di seguito “il Cliente”). Il servizio è
          destinato esclusivamente a soggetti che agiscono nell'esercizio della propria
          attività professionale: <strong>non è un servizio rivolto ai consumatori</strong>.
        </p>
        <p>
          Le clienti finali che prenotano online tramite la pagina di un centro non
          stipulano un contratto con il Fornitore: il loro rapporto è con il centro
          estetico che le ha invitate.
        </p>
      </Section>

      <Section n={2} title="Il servizio">
        <p>
          AURA è un software gestionale erogato in modalità SaaS (accesso via browser e app)
          che comprende, secondo il piano attivato, agenda e prenotazioni, anagrafica
          clienti, catalogo trattamenti, magazzino, programma fedeltà, preventivi e vendita
          al banco, oltre a funzioni assistite da intelligenza artificiale descritte nella{" "}
          <Link to="/intelligenza-artificiale" className="underline underline-offset-4">
            pagina dedicata
          </Link>
          .
        </p>
        <p>
          Il Fornitore concede al Cliente una licenza d'uso non esclusiva, non trasferibile
          e revocabile, limitata alla durata del contratto. Il software resta di proprietà
          del Fornitore: non è ceduto né venduto.
        </p>
      </Section>

      <Section n={3} title="Attivazione, prezzi e pagamenti">
        <p>
          I piani sono proposti su preventivo personalizzato. Il contratto si perfeziona
          con l'accettazione scritta del preventivo. I corrispettivi indicati nei preventivi
          si intendono <strong>al netto dell'IVA</strong>, salvo diversa indicazione.
        </p>
        <p>
          Il periodo di prova eventualmente concesso è gratuito e non impegnativo: al
          termine, in assenza di attivazione di un piano, l'accesso viene sospeso e i dati
          conservati per 30 giorni prima della cancellazione.
        </p>
        <p>
          Il mancato pagamento oltre 30 giorni dalla scadenza legittima la sospensione del
          servizio, previo avviso scritto con almeno 7 giorni di preavviso.
        </p>
      </Section>

      <Section n={4} title="Obblighi del Cliente">
        <List
          items={[
            "custodire le credenziali di accesso e non condividerle con persone non autorizzate, rispondendo delle attività svolte con le proprie utenze;",
            "inserire nella piattaforma solo dati di cui ha titolo a disporre, avendo raccolto le informative e i consensi necessari dalle proprie clienti;",
            "utilizzare il servizio nel rispetto della legge, in particolare della normativa su protezione dei dati, marketing diretto e tutela del consumatore;",
            "non tentare di accedere ad aree o dati di altri clienti, non decompilare il software, non effettuare test di sicurezza senza autorizzazione scritta;",
            "mantenere aggiornati i propri dati di fatturazione e contatto.",
          ]}
        />
        <p>
          Il Cliente è titolare del trattamento dei dati delle proprie clienti; il Fornitore
          agisce come responsabile ai sensi dell'art. 28 GDPR sulla base di un accordo
          separato che costituisce parte integrante del contratto.
        </p>
      </Section>

      <Section n={5} title="Disponibilità e manutenzione">
        <p>
          Il Fornitore si impegna a mantenere il servizio disponibile con continuità,
          adottando la diligenza professionale richiesta. Non è tuttavia garantito un
          funzionamento ininterrotto e privo di errori: possono verificarsi interruzioni per
          manutenzione programmata (comunicata con ragionevole preavviso), interventi
          urgenti di sicurezza o cause non imputabili al Fornitore, inclusi i disservizi dei
          fornitori di infrastruttura.
        </p>
        <p>
          Eventuali livelli di servizio garantiti (SLA) sono validi solo se espressamente
          previsti nel preventivo accettato.
        </p>
      </Section>

      <Section n={6} title="Proprietà dei dati e portabilità">
        <p>
          I dati inseriti dal Cliente restano di sua esclusiva proprietà. Il Fornitore non
          li utilizza per finalità proprie, non li cede a terzi e non li impiega per
          addestrare modelli di intelligenza artificiale.
        </p>
        <p>
          In qualsiasi momento durante il contratto, e per 30 giorni dopo la cessazione, il
          Cliente può richiedere l'esportazione dei propri dati in formato strutturato.
          Trascorso tale termine i dati vengono cancellati definitivamente, salvo quanto
          debba essere conservato per obblighi di legge.
        </p>
      </Section>

      <Section n={7} title="Limitazione di responsabilità">
        <p>
          Il Fornitore risponde dei danni diretti imputabili a propria colpa. Salvo i casi
          di dolo o colpa grave, e salvo i danni alla persona, la responsabilità complessiva
          del Fornitore è limitata ai corrispettivi versati dal Cliente nei dodici mesi
          precedenti l'evento.
        </p>
        <p>
          Il Fornitore non risponde della perdita di dati causata da un uso improprio della
          piattaforma da parte del Cliente, né delle scelte commerciali o professionali che
          il Cliente compie sulla base delle informazioni e dei suggerimenti prodotti dal
          software, incluse le funzioni assistite da intelligenza artificiale, che hanno
          natura di supporto e non sostituiscono la valutazione professionale.
        </p>
      </Section>

      <Section n={8} title="Durata e recesso">
        <p>
          Il contratto ha la durata indicata nel preventivo accettato e si rinnova salvo
          disdetta comunicata per iscritto con almeno 30 giorni di preavviso rispetto alla
          scadenza.
        </p>
        <p>
          Il Fornitore può recedere per giusta causa, con effetto immediato, in caso di
          violazione grave degli obblighi del Cliente, in particolare un uso della
          piattaforma contrario alla legge o lesivo dei diritti di terzi.
        </p>
      </Section>

      <Section n={9} title="Modifiche ai termini">
        <p>
          Il Fornitore può aggiornare questi termini per adeguarli a modifiche del servizio
          o della normativa. Le modifiche sostanziali sono comunicate con almeno 30 giorni
          di preavviso: entro tale termine il Cliente può recedere senza penali se non le
          accetta.
        </p>
      </Section>

      <Section n={10} title="Legge applicabile e foro">
        <p>
          Il contratto è regolato dalla legge italiana. Per ogni controversia è competente
          in via esclusiva il foro del luogo in cui ha sede il Fornitore, salvo diversa
          norma inderogabile.
        </p>
      </Section>
    </LegalLayout>
  );
}
